import getWidthWithoutPadding from '../../helpers/getWidthWithoutPadding';
import isMobile, {isSmallContent} from '../../helpers/isMobile';
import lazyload from '../../helpers/lazyload';
import tabTrap from '../../helpers/tabTrap';
import trapFocus from '../../helpers/trapFocus';
import triggerExploreScroll from '../../helpers/triggerExploreScroll';
import triggerViewRecord from '../../helpers/triggerViewRecord';
import {gsap} from '../../vendors/gsap';
import {Flip} from '../../vendors/gsap/Flip';
import appData from '../core/appData';

import {setMobileMenuHeight} from '../menu/index';
import zoom from '../zoom/index';
import {expandImageAnimation} from './storyViewAnimation';

import createMainCarousel from './createMainCarousel/index';
import createRelatedCarousel from './createRelatedCarousel/index';
import createTagCarousel from './createTagCarousel/index';
import createTextCarousel from './createTextCarousel/index';

import './storyView.css';

gsap.registerPlugin(Flip);

/**
 * This module contains data and functions to handle timeline view
 *
 * @returns {undefined}
 */
const storyView = () => {
    let storyContainer = null;
    let storyData = [];
    let tl = null;
    let tf = null;
    let triggerEl = null;
    let triggerContainer = null;
    let viewMode = '';
    let isInitialized = false;
    let isExploreViewHidden = Boolean(document.querySelector('.explore-container.hidden'));
    let isFilterActive = !isExploreViewHidden && Boolean(document.querySelector('.filter-open.active'));
    let escKeyHandlerAttached = false;
    const outsideElements =
        'a:not(.header a):not(.UsableNetAssistive), button:not(.hamburger):not([data-action="chapters"]), .swiper-button-next, .swiper-button-prev, .skip-link,.app-chapters-menu,.timeline-container,.explore-container,.js-onboarding-wizard';

    /**
     *
     * @param {string} realIndex - active slide index
     */
    const toggleActiveStoryCard = realIndex => {
        const slides = document.querySelectorAll('.related-story-carousel .swiper-slide');

        slides.forEach(slide => {
            const storyCard = slide.querySelector('.story-card');
            if (!storyCard) return;

            const slideIndex = Number(slide.getAttribute('data-swiper-slide-index'));

            if (slideIndex === realIndex) {
                storyCard.classList.add('active');
            } else {
                storyCard.classList.remove('active');
            }
        });
    };

    /**
     * Reset trigger page info
     */
    const resetTriggerPageInfo = () => {
        triggerEl = null;
        triggerContainer = null;
    };

    /**
     *  Return trigger page info
     */
    const setTriggerPageInfo = () => {
        if (!triggerEl) return;

        triggerContainer = triggerEl.closest('.explore-tile');
        viewMode = 'explore';

        if (!triggerContainer) {
            triggerContainer = triggerEl.closest('.timeline-card');
            viewMode = 'timeline';
        }
    };

    /**
     * Sets the timeline container HTML element to a variable
     */
    const setStoryContainer = () => {
        storyContainer = document.querySelector('.story-container');
    };

    /**
     * Remove elements to be shown on the page
     */
    const clearContent = () => {
        storyContainer.replaceChildren();
    };

    /**
     * Set disabled attribute for related carousel button
     *
     * @param {boolean} disabled - disabled attribute value to set
     */
    const setDisabledAttrForNavBtns = disabled => {
        if (disabled) {
            document.querySelector('.related-nav-next')?.setAttribute('disabled', true);
            document.querySelector('.related-nav-prev')?.setAttribute('disabled', true);
        } else {
            document.querySelector('.related-nav-next')?.removeAttribute('disabled');
            document.querySelector('.related-nav-prev')?.removeAttribute('disabled');
        }
    };

    /**
     * Handles the case when slides are less
     *
     * @param {Object} config - config object
     * @param {Object} [config.relatedSwiper] - related swiper object
     * @param {Object} [config.relatedDraggable] - related swiper drag handler
     * @param {Object} [config.mainSwiper] - main swiper object
     * @param {Object} [config.mainDraggable] - main swiper drag handler
     * @param {HTMLElement} [config.relatedViewContainer] - swiper container html element
     */
    const relatedSwiperLessSlidesCheck = ({
        relatedSwiper,
        relatedDraggable,
        mainSwiper,
        mainDraggable,
        relatedViewContainer
    }) => {
        const relatedContainerWidth = getWidthWithoutPadding(relatedViewContainer);
        const relatedSwiperSlides = document.querySelectorAll('.related-story-carousel .swiper-slide');
        const heading = document.querySelector('.related-story-heading ');
        const extraSpace = window.innerWidth <= 1024 ? 4 : 8; // for padding
        const singleSlideWidth = 132; // slides are of fixed width hence the magic number - 132 (width + margin)
        const slidesWidth = singleSlideWidth * relatedSwiperSlides.length + extraSpace;

        if (!relatedSwiperSlides?.length || relatedSwiperSlides.length <= 1) {
            mainSwiper?.disable();
            mainDraggable?.disable();
            relatedSwiper?.disable();
            relatedDraggable?.disable();
            setDisabledAttrForNavBtns(true);
        }

        if (slidesWidth < relatedContainerWidth) {
            let valueToSubtract = slidesWidth;
            if (heading.clientWidth > slidesWidth) {
                valueToSubtract = heading.clientWidth + extraSpace;
            }
            const edgeWidth = relatedContainerWidth - valueToSubtract;
            gsap.set('.related-story-edge', {autoAlpha: 1, width: edgeWidth});
            relatedSwiper?.disable();
            relatedDraggable?.disable();
            setDisabledAttrForNavBtns(true);
        } else {
            gsap.set('.related-story-edge', {autoAlpha: 0, width: 0});
            relatedSwiper?.enable();
            relatedDraggable?.enable();
            relatedSwiper?.slideToLoop(mainSwiper.realIndex, 0);
            setDisabledAttrForNavBtns(false);
        }
    };

    /**
     * Story view reveal animation
     */
    const initialAnimation = () => {
        tl = gsap.timeline({
            /**
             * Executes before animation start
             */
            onStart() {
                const storyTopView = document.querySelector('.story-top-view');
                storyTopView.classList.remove('hide');

                // remove the border in the beginning and add it back after animation complete
                document.querySelector('.story-main-view').classList.add('!border-none');

                // hide the close, link, carousel navigation and show it after animation complete
                gsap.set('.slide-icon', {autoAlpha: 0});

                // on close animation, active and next slides are being hidden so it is set to visible again.
                // this code can be placed in onReverseComplete (if required)
                gsap.set('.main-carousel .swiper-slide-active', {visibility: 'visible'});
                gsap.set('.main-carousel .swiper-slide-next', {visibility: 'visible'});
            },
            /**
             * Executes after animation complete
             */
            onComplete() {
                const container = document.querySelector('.story-details-container');
                container.style = '';
                container.style.left = 0;
                container.style.width = 'auto';
                container.classList.remove('absolute');
                document.querySelector('.story-main-view').classList.remove('!border-none');
                gsap.to('.slide-icon', {autoAlpha: 1, duration: 0.3});

                // set focus on main container
                document.querySelector('.app-content')?.focus();
            },
            /**
             * Executes after reverse animation complete
             */
            onReverseComplete() {
                const container = document.querySelector('.story-container');
                const shouldEmpty = container.dataset.shouldEmpty === 'true';
                if (shouldEmpty) {
                    container.replaceChildren();
                    container.classList.add('hidden');
                    container.removeAttribute('data-should-empty');
                }
            },
            paused: true
        });
        tl.from(
            '.story-bottom-view',
            {
                yPercent: 80,
                autoAlpha: 0,
                ease: 'power1.in'
            },
            '<'
        )
            .fromTo('.story-details-container', {left: '90%'}, {left: 0, ease: 'power1.in'}, '<')
            .fromTo('.related-story-line', {width: '50%'}, {width: '100%', ease: 'power1.out', duration: 0.3}, '<')
            .fromTo(
                '.story-tags-wrapper',
                {height: 0},
                {
                    height: 'auto',
                    duration: 0.1,
                    ease: 'power1.inOut',
                    delay: 0.4
                },
                '<'
            );
    };

    /**
     * open story animation
     */
    const openStoryAnimation = () => {
        tl.play();

        // hide filters menu when individual record is activated
        if (document.querySelector('.app-filters') && document.querySelector('.app-zoom')?.style.opacity < 1) {
            gsap.fromTo(
                '.app-filters',
                {autoAlpha: 1, y: 0},
                {autoAlpha: 0, y: '100%', duration: 0.3, ease: 'power:in'}
            );
        }
    };

    /**
     * close story animation
     */
    const closeStoryAnimation = () => {
        // hide the close, link, carousel navigation
        gsap.set('.slide-icon', {autoAlpha: 0});

        // active and next slides are being hidden to make animation look good
        gsap.set('.main-carousel .swiper-slide-active', {visibility: 'hidden'});
        gsap.set('.main-carousel .swiper-slide-next', {visibility: 'hidden'});

        // animate
        tl.reverse();
    };

    /**
     * on close story callback
     *
     * @param {Object} config - params object
     * @returns {undefined}
     */
    const onCloseHandler = config => {
        const {updateURL = true, newRoute} = config ?? {};
        const detailsContainer = document.querySelector('.story-details-container');
        if (!detailsContainer) return;

        const container = document.querySelector('.story-container');
        container.setAttribute('data-should-empty', true);
        const wrapper = document.querySelector('.story-details-wrapper');
        wrapper.style.height = `${detailsContainer.getBoundingClientRect().height}px`;
        detailsContainer.classList.add('absolute', 'w-full');
        const storyTopView = document.querySelector('.story-top-view');
        storyTopView.classList.add('hide');
        closeStoryAnimation();

        // show filters menu when individual record is closed
        if (document.querySelector('.app-filters')) {
            gsap.fromTo(
                '.app-filters',
                {autoAlpha: 0, y: '100%'},
                {autoAlpha: 1, y: 0, duration: 0.3, ease: 'power:in'}
            );
        }
        // show chapters menu and resume scroll on explore view
        if (!isExploreViewHidden) {
            // Explore view has the collapsed chapters menu
            let toggleToCollapsed = !isSmallContent();
            let chaptersMenu = !isSmallContent();

            // But are we going back to the Explore?
            if (newRoute && newRoute !== 'exploreView') {
                toggleToCollapsed = false;
                chaptersMenu = false;
            }

            window.dispatchEvent(new CustomEvent('250.chapters', {detail: {chaptersMenu, toggleToCollapsed}}));
            triggerExploreScroll(false);
        }

        tf.onClose();
        // shrinkImageAnimation({triggerContainer, viewMode});
        setMobileMenuHeight('tall');
        tabTrap.disable(outsideElements);

        window.dispatchEvent(new CustomEvent('250.close.story', {detail: {updateURL: updateURL}}));
    };

    /**
     * Create elements to be shown on the page
     */
    const loadContent = () => {
        // Obtain a clone of the template
        const templateStoryView = document.querySelector('#storyViewContainer');
        const html = templateStoryView.content.cloneNode(true);

        // set zoom story data
        appData.setZoomStoryData([...storyData]);

        // Set the related count
        const isUpdating = Boolean(storyContainer.firstChild);

        if (isUpdating) {
            clearContent();
        }

        storyContainer.append(html);

        // zoom feature
        const {onZoomOpenClick, onZoomCloseClick, onZoomCloseClickEvent} = zoom({resetTriggerPageInfo});

        /**
         * on zoom story callback
         *
         * @param {number} index - slide index
         */
        const onZoomHandler = index => {
            closeStoryAnimation();

            // get active slide
            const activeContainer = document.querySelector('.main-carousel .swiper-slide-active');

            // clone active image and hide it
            const node = activeContainer.querySelector('img');
            const clone = node.cloneNode(true);
            clone.classList.add('hidden');

            // add zoom image class to the active image
            node.classList.add('zoom-image');

            // for zoom expand collapse landscape view
            if (isMobile()) {
                node.classList.add('!h-auto');
            }

            // append to active container
            activeContainer.append(clone);

            // set height of container and hide active image in zoom container
            const zoomItems = document.querySelector('.zoom-wrapper').children;
            const zoomActiveItem = zoomItems.item(index);
            const zoomActiveImg = zoomActiveItem.querySelector('.zoom-image');
            const imageContainer = zoomActiveItem.querySelector('div.zoom-card-image');
            gsap.set(imageContainer, {
                height: gsap.getProperty(zoomActiveImg, 'height'),
                width: gsap.getProperty(zoomActiveImg, 'width')
            });
            zoomActiveImg.classList.add('hidden');

            onZoomOpenClick(index);

            // animate
            // hide header
            const headerEl = document.querySelector('.header');
            if (headerEl) {
                gsap.fromTo('.header', {y: 0}, {y: '-100%'});
                headerEl.classList.add('zoom-open');
            }

            // hide app menu
            const menuEl = document.querySelector('.app-menu');
            if (menuEl) {
                gsap.fromTo('.app-menu', {y: 0}, {y: '100%', duration: 0.3});
                menuEl.classList.add('zoom-open');
            }

            // add classes to put zoom view behind indivitual story view
            const appZoom = document.querySelector('.app-zoom');
            const storyTopView = document.querySelector('.story-top-view');
            storyTopView.classList.add('hide');
            gsap.set(appZoom, {autoAlpha: 1});
            appZoom.classList.add('z-6010');

            // lazy load images if any
            lazyload.load();

            const state = Flip.getState(node);

            // move to expanded image
            imageContainer.append(node);

            Flip.from(state, {
                duration: 0.7,
                ease: 'power2.inOut',
                absolute: true,
                scale: true,
                /**
                 * oncomplete callback
                 */
                onComplete() {
                    node.remove();
                    clone.classList.remove('hidden');
                    zoomActiveImg.classList.remove('hidden');
                    imageContainer.removeAttribute('style');
                    appZoom.classList.remove('z-6010');
                }
            });
        };

        const relatedViewContainer = document.querySelector('.related-story-content');
        const {relatedSwiper, relatedDragHandler} = createRelatedCarousel({
            el: relatedViewContainer,
            labelPrevious: relatedViewContainer.dataset.labelPrevious,
            labelNext: relatedViewContainer.dataset.labelNext,
            storyData
        });

        const {slideUp, slideDown, slideTo: textSlideTo} = createTextCarousel({storyData});

        // main carousel
        const mainViewContainer = storyContainer.querySelector('.story-main-view');
        const labels = {
            previous: mainViewContainer.dataset.labelPrevious,
            next: mainViewContainer.dataset.labelNext,
            close: mainViewContainer.dataset.labelClose,
            zoomIn: mainViewContainer.dataset.labelZoomIn,
            featuredLink: mainViewContainer.dataset.labelFeaturedLink
        };

        const {updateTags} = createTagCarousel({storyData, labels});

        const {mainSwiper, mainDragHandler} = createMainCarousel({
            el: mainViewContainer,
            storyData,
            swiper: relatedSwiper,
            labelPrevious: labels.previous,
            labelNext: labels.next,
            labelClose: labels.close,
            labelZoomIn: labels.zoomIn,
            labelFeaturedLink: labels.featuredLink,
            /**
             * on close story callback
             */
            onClose: onCloseHandler,
            /**
             * on zoom story callback
             */
            onZoom: onZoomHandler
        });

        // load images
        lazyload.load();

        /**
         * this will handle story next btn click on the related slider
         */
        function slideNextRelated() {
            if (mainSwiper.animating || relatedSwiper.animating) return;

            relatedSwiper?.slideNext();
        }

        /**
         * this will handle story prev btn click on the related slider
         */
        function slidePrevRelated() {
            if (mainSwiper.animating || relatedSwiper.animating) return;
            relatedSwiper?.slidePrev();
        }

        /**
         * this will handle story next btn click on the main slider
         */
        function slideNextMain() {
            if (mainSwiper.animating || relatedSwiper.animating) return;

            mainSwiper?.slideNext();
            relatedSwiper?.slideNext();
            slideUp();
            updateTags('next');

            // Update the active story
            if (mainSwiper && mainSwiper.realIndex <= storyData.length) {
                appData.setActiveStory(storyData[mainSwiper.realIndex].id);
            }
        }

        /**
         * this will handle story prev btn click on the main slider
         */
        function slidePrevMain() {
            if (mainSwiper.animating || relatedSwiper.animating) return;

            mainSwiper?.slidePrev();
            relatedSwiper?.slidePrev();
            slideDown();
            updateTags('prev');

            // Update the active story
            if (mainSwiper && mainSwiper.realIndex <= storyData.length) {
                appData.setActiveStory(storyData[mainSwiper.realIndex].id);
            }
        }

        // related carousel next prev button click handling
        const nextBtn = document.querySelector('.related-navigation .prev');
        const prevBtn = document.querySelector('.related-navigation .next');
        nextBtn.onclick = slidePrevRelated;
        prevBtn.onclick = slideNextRelated;

        // handling related story drag
        const relatedDraggable = relatedDragHandler({slideNextRelated, slidePrevRelated})[0];

        // main carousel next prev button click handling
        const mainNextBtn = document.querySelector('.main-carousel .prev-btn');
        const mainPrevBtn = document.querySelector('.main-carousel .next-btn');
        mainNextBtn.onclick = slideNextMain;
        mainPrevBtn.onclick = slidePrevMain;

        // handling main story drag
        const mainDraggable = mainDragHandler({slideNextMain, slidePrevMain})[0];

        storyContainer.classList.remove('hidden');

        // if slides are less in number
        relatedSwiperLessSlidesCheck({
            relatedSwiper,
            relatedDraggable,
            mainSwiper,
            mainDraggable,
            relatedViewContainer
        });

        initialAnimation();

        // explore/timeline image move-in animation
        expandImageAnimation({triggerContainer, viewMode});

        if (isUpdating) {
            tl.progress(1).pause();
        } else {
            openStoryAnimation();
        }

        tf = trapFocus(storyContainer);

        document.querySelector('.story-empty-area').onclick = onCloseHandler;

        document.querySelector('.story-details-view').addEventListener('click', e => {
            if (!e.target.closest('.story-details-wrapper')) {
                onCloseHandler();
            }
        });

        /**
         * Updates all slides to a specificed index
         *
         * @param {number} slideIndex - slide index
         * @param {boolean} complete - if true immediately complete the animation
         */
        const slideAll = (slideIndex, complete) => {
            mainSwiper.slideToLoop(slideIndex, complete ? 0 : 700);
            textSlideTo(slideIndex, complete);
            updateTags('', slideIndex, complete);
            relatedSwiper.slideToLoop(slideIndex, complete ? 0 : 700);
        };

        /**
         * zoom expand close animation
         */
        const onZoomCloseAnimation = () => {
            openStoryAnimation();

            const mainCarouselContainer = document.querySelector('.main-carousel');

            // hide prev slides so that overflow visible will not show it
            mainCarouselContainer.classList.add('hide-prev');

            // get active slide index
            const mainActiveSlideContainer = mainCarouselContainer.querySelector('.swiper-slide-active');
            const slideIndex = mainActiveSlideContainer.dataset.swiperSlideIndex;

            // get active slide in zoom view
            const zoomItems = document.querySelector('.zoom-wrapper').children;
            const zoomActiveContainer = zoomItems.item(slideIndex);

            // clone active image and hide it in zoom view
            const node = zoomActiveContainer.querySelector('.zoom-image');
            const clone = node.cloneNode(true);
            clone.classList.add('hidden');

            // set height of container
            const imageContainer = zoomActiveContainer.querySelector('.zoom-card-image');
            gsap.set(imageContainer, {
                height: gsap.getProperty(node, 'height'),
                width: gsap.getProperty(node, 'width')
            });

            // append cloned image to active container in zoom carousel
            imageContainer.append(clone);

            // add classes and hide active image in main carousel
            const mainCarouselSwiper = document.querySelector('.main-carousel .swiper');
            mainCarouselContainer.classList.add('!overflow-visible');
            mainCarouselSwiper.classList.add('!overflow-visible');
            const mainActiveSlideImage = mainActiveSlideContainer.querySelector('img');
            mainActiveSlideImage.classList.add('hidden');

            // put zoom view behind story view and hide it once animation is complete
            const appZoom = document.querySelector('.app-zoom');
            appZoom.classList.add('z-6010');

            // animate
            // show header
            const headerEl = document.querySelector('.header');
            if (headerEl) {
                gsap.fromTo('.header', {y: '-100%'}, {y: 0});
                headerEl.classList.remove('zoom-open');
            }

            // show app menu
            const menuEl = document.querySelector('.app-menu');
            if (menuEl) {
                gsap.fromTo('.app-menu', {y: '100%'}, {y: 0, duration: 0.3});
                menuEl.classList.remove('zoom-open');
            }

            // for zoom expand collapse landscape view
            if (isMobile()) {
                node.classList.add('!h-auto');
            }

            const state = Flip.getState(node);

            // move image from expanded view to main view
            mainActiveSlideContainer.append(node);

            Flip.from(state, {
                duration: 0.7,
                ease: 'power2.inOut',
                absolute: true,
                scale: true,

                /**
                 * oncomplete callback
                 */
                onComplete() {
                    mainSwiper.off('slideChangeTransitionEnd', onZoomCloseAnimation);

                    node.remove();
                    clone.classList.remove('hidden');
                    mainActiveSlideImage.classList.remove('hidden');
                    mainCarouselContainer.classList.remove('!overflow-visible');
                    mainCarouselSwiper.classList.remove('!overflow-visible');
                    mainCarouselContainer.classList.remove('hide-prev');
                    imageContainer.removeAttribute('style');
                    appZoom.classList.remove('z-6010');
                    gsap.set(appZoom, {autoAlpha: 0});
                }
            });
        };

        /**
         *
         * @param {number} slideIndex - slide index
         */
        const onZoomCloseCallback = slideIndex => {
            // case - closing expand view without scrolling
            if (slideIndex === mainSwiper.realIndex) {
                onZoomCloseAnimation();
            }
            // case - expand view is scrolled and active slide is changed
            else {
                mainSwiper.on('slideChangeTransitionEnd', onZoomCloseAnimation);
                slideAll(slideIndex, true);
            }
        };

        /**
         * Handle closing view with Esc key
         *
         * @param {event} event - event
         */
        const escKeyHandler = event => {
            const zoomView = document.querySelector('.zoom-view');
            const zoomViewHidden = zoomView.style.visibility === 'hidden';

            if (event.key === 'Escape') {
                if (zoomViewHidden) {
                    onCloseHandler();
                    window.removeEventListener('keydown', escKeyHandler);
                    escKeyHandlerAttached = false;
                } else {
                    onZoomCloseClick(onZoomCloseCallback);
                }
            }
        };

        if (!escKeyHandlerAttached) {
            window.addEventListener('keydown', escKeyHandler);
            escKeyHandlerAttached = true;
        }

        // zoom close event
        onZoomCloseClickEvent(onZoomCloseCallback);

        /**
         * Related story card click handler
         *
         * @param {Event} e - click event
         */
        const relatedStoryCardClick = e => {
            const el = e.target.closest('.swiper-slide');

            if (
                !el ||
                mainSwiper.animating ||
                relatedSwiper.animating ||
                (isFilterActive && document.querySelector('.related-story-edge').clientWidth > 0)
            ) {
                return;
            }

            const slideIndex = Number(el.dataset.swiperSlideIndex);

            if (!isFilterActive) {
                resetTriggerPageInfo();
                triggerViewRecord(e, slideIndex);
            } else {
                slideAll(slideIndex);
            }
        };

        // related carousel slide click
        const relatedStoryCarousel = document.querySelector('.related-story-carousel');
        relatedStoryCarousel.addEventListener('click', relatedStoryCardClick);

        /**
         * Main story slide image click expand functionality
         *
         * @param {Event} e - click event
         */
        const mainCarouseSlideClick = e => {
            const activeImage = e.target.closest('.swiper-slide-active img');

            // if image is clicked show expand view
            if (activeImage) {
                const activeSlide = activeImage.parentElement;
                const slideIndex = Number(activeSlide.dataset.swiperSlideIndex);

                onZoomHandler(slideIndex);
            } else {
                // if slide is clicked, focus on the selected slide
                const el = e.target.closest('.swiper-slide');
                if (!el || mainSwiper.animating || relatedSwiper.animating) {
                    return;
                }
                const slideIndex = Number(el.dataset.swiperSlideIndex);
                slideAll(slideIndex);
            }
        };

        // main carousel slide click
        const mainCarousel = document.querySelector('.main-carousel');
        mainCarousel.addEventListener('click', mainCarouseSlideClick);

        // slide featured link update on slide change
        mainSwiper.on('activeIndexChange', function ({realIndex}) {
            const featuredLink = storyData[realIndex]?.featuredLink;
            const featuredLinkBtn = document.querySelector('.link-btn');

            toggleActiveStoryCard(realIndex);

            if (featuredLink) {
                featuredLinkBtn.setAttribute('href', featuredLink);
                featuredLinkBtn.classList.remove('hidden');

                const ariaLabel = labels.featuredLink.replace('{0}', storyData[realIndex]?.models[0] ?? '');
                featuredLinkBtn.setAttribute('aria-label', ariaLabel);
                featuredLinkBtn.setAttribute('title', ariaLabel);
            } else {
                featuredLinkBtn.classList.add('hidden');
                featuredLinkBtn.setAttribute('href', '#');
            }

            // 2024-08-23: Feature link is diabled for now
            featuredLinkBtn.classList.add('hidden');
        });

        /**
         * Closes the zoomed view on zoom container empty area click
         *
         * @param {Event} e - zoom container click event
         */
        const onZoomContainerClick = e => {
            if (e.target.classList.contains('zoom-wrapper')) {
                onZoomCloseClick(onZoomCloseCallback);
            }
        };

        // zoom container click listener
        const zoomContainer = document.querySelector('.zoom-container');
        zoomContainer.addEventListener('click', onZoomContainerClick);

        // adding class while related swiper is animating
        relatedSwiper.on('slideChangeTransitionStart', () => {
            relatedSwiper.el.classList.add('animating');
        });
        relatedSwiper.on('slideChangeTransitionEnd', () => {
            relatedSwiper.el.classList.remove('animating');
        });

        window.addEventListener('resize', () =>
            relatedSwiperLessSlidesCheck({
                relatedSwiper,
                relatedDraggable,
                mainSwiper,
                mainDraggable,
                relatedViewContainer
            })
        );
    };

    /**
     * Handles the content worker done event
     *
     * @param {Object} event Custom event that contains the content worker result details
     */
    const handleContentWorkerDone = event => {
        if (event && event.detail) {
            // Worker is initialized and we are good to with loading the page
            if (event.detail.action === 'related') {
                storyData.push(...event.detail.result);
                loadContent();
            }
        }
    };

    /**
     *  Initial data setup and event listeners
     *
     * @param {Object} activeStoryData - activated story
     * @param {Object} targetEl - clicked individual story html item
     */
    const init = (activeStoryData, targetEl) => {
        triggerEl = targetEl;
        setTriggerPageInfo();
        setStoryContainer();
        storyData = [activeStoryData];

        // Only need one listener, ever
        if (!isInitialized) {
            window.addEventListener('content.worker.done', handleContentWorkerDone);

            isInitialized = true;
        }

        setMobileMenuHeight('short');

        isExploreViewHidden = Boolean(document.querySelector('.explore-container.hidden'));
        isFilterActive = !isExploreViewHidden && Boolean(document.querySelector('.filter-open.active'));

        // load related content items
        if (!isFilterActive) {
            appData.triggerRelatedData(activeStoryData);
        } else {
            storyData = appData.getViewData();
            // place the selected story at the start
            if (appData.getViewData()?.length > 1) {
                const data = [...appData.getViewData()];
                const index = data.findIndex(item => item.id === activeStoryData.id);
                data.splice(index, 1);
                storyData = [activeStoryData, ...data.slice(0, 19)];
            }
            loadContent();
        }

        tabTrap.enable(outsideElements);
    };

    return {
        init,
        clearContent,
        onCloseHandler
    };
};

export default storyView();
